import ExampleConfigForm from "@/components/common/featureManagementGrid/exampleConfigForm";
import {
  WaitlistIcon,
  UserIcon,
  Icon,
  UserCheckStrokeStandardIcon,
  MoneyReceiveCircleIcon,
  BotIcon,
  Clock01StandardIcon,
  MoneyBagStandardIcon,
  UserCheckBulkIcon,
  MoneyReceiveBulkIcon,
  WaitlistBulkIcon,
  BotBulkIcon,
  MoneyBagBulkIcon,
  Clock01BulkIcon,
} from "@/components/common/icons";
import {
  AMBER,
  DARK_PURPLE,
  GREY,
  LIGHT_BLUE,
  OCEAN_BLUE,
  PINK,
  TEAL,
} from "@/config/mui/colorPalette";
import { FeatureFlag, FeatureFlags } from "./featureFlags";

export const FeatureManagementFlags = {
  CHECK_IN_ALERTS: "check-in-alerts",
  SALES_ATTRIBUTION: "sales-attribution",
  WAITLIST: "waitlist",
  MOXIE_AI_BOOKING_ASSISTANT: "moxie-ai-booking-assistant",
  DEPOSITS: "deposits",
  CLOCK_IN_OUT: "clock-in-out",
} as const;

export type FeatureManagementFlag =
  (typeof FeatureManagementFlags)[keyof typeof FeatureManagementFlags];

// Comprehensive registry for feature management flags
type FeatureManagementRegistry = {
  [K in FeatureManagementFlag]: {
    icon: React.ComponentType<Icon>;
    bulkIcon: React.ComponentType<Icon>;
    iconColor: string;
    iconBackground: string;
    configComponent?: React.ReactNode;
    turnOnModalMessage?: string;
    turnOffModalMessage?: string;
    drawerDetails?: string; // TODO-Feature Management: remove this after next deployment (29.07.2025)
    requiredFeatureFlag?: FeatureFlag;
  };
};

const FEATURE_MANAGEMENT_REGISTRY: FeatureManagementRegistry = {
  [FeatureManagementFlags.CHECK_IN_ALERTS]: {
    icon: UserCheckStrokeStandardIcon,
    bulkIcon: UserCheckBulkIcon,
    iconColor: TEAL[80],
    iconBackground: TEAL[20],
    turnOnModalMessage:
      "Check-in alerts will be enabled for your practice. You'll receive real-time notifications when clients check in for their appointments. Staff members will be notified through the app and other configured channels.",
    turnOffModalMessage:
      "Check-in alerts will be turned off completely. You will no longer receive notifications when clients check in for appointments. This will not affect the check-in functionality itself, only the alert notifications.",
    drawerDetails:
      "Check-in alerts notify providers as soon as a client checks in for their appointment. This helps streamline workflows and ensures timely service delivery.",
  },
  [FeatureManagementFlags.SALES_ATTRIBUTION]: {
    icon: MoneyReceiveCircleIcon,
    bulkIcon: MoneyReceiveBulkIcon,
    iconColor: AMBER[80],
    iconBackground: AMBER[20],
    drawerDetails:
      "Sales attribution allows you to assign revenue from individual invoice items to specific provider. This helps track performance, streamline commission reporting, and ensure accurate revenue distribution.",
  },
  [FeatureManagementFlags.WAITLIST]: {
    icon: WaitlistIcon,
    bulkIcon: WaitlistBulkIcon,
    iconColor: LIGHT_BLUE[80],
    iconBackground: LIGHT_BLUE[20],
    turnOnModalMessage:
      "Waitlist will be enabled for your practice. Clients will be able to join waitlists for fully booked appointments and you'll be able to manage waitlist notifications. This feature will appear in your booking flow and scheduling interface.",
    turnOffModalMessage:
      "Waitlist will be disabled completely. Clients will no longer be able to join waitlists and existing waitlist entries will remain but won't be accessible through the interface. You can re-enable this feature at any time.",
    requiredFeatureFlag: FeatureFlags.WAITLIST_V1,
    drawerDetails:
      "Clients can sign up for a waitlist when no appointments are available. Providers can quickly notify waitlisted clients when a slot becomes free. Easily fill last-minute openings and reduce gaps.",
  },
  [FeatureManagementFlags.MOXIE_AI_BOOKING_ASSISTANT]: {
    icon: BotIcon,
    bulkIcon: BotBulkIcon,
    iconColor: PINK[80],
    iconBackground: PINK[20],
  },
  [FeatureManagementFlags.DEPOSITS]: {
    icon: MoneyBagStandardIcon,
    bulkIcon: MoneyBagBulkIcon,
    iconColor: DARK_PURPLE[80],
    iconBackground: DARK_PURPLE[20],
    configComponent: <ExampleConfigForm />,
    turnOnModalMessage:
      "Deposits will be enabled for your practice. Clients will be required to pay a deposit when booking appointments both on the scheduling page and during online booking. You can configure the deposit settings after enabling this feature.",
    turnOffModalMessage:
      "Deposits will be turned off completely. They cannot be collected both on the scheduling page and during online booking. Your current settings will be saved in case you re-enable it later.",
  },
  [FeatureManagementFlags.CLOCK_IN_OUT]: {
    icon: Clock01StandardIcon,
    bulkIcon: Clock01BulkIcon,
    iconColor: OCEAN_BLUE[80],
    iconBackground: OCEAN_BLUE[20],
  },
};

/**
 * Get the configuration component for a given feature management flag
 * @param flag - The feature management flag
 * @returns The React component for configuring the feature, or undefined if not available
 */
export function getFeatureManagementConfig(
  flag: FeatureManagementFlag
): React.ReactNode | undefined {
  return FEATURE_MANAGEMENT_REGISTRY[flag]?.configComponent;
}

/**
 * Get the icon and color configuration for a given feature management flag
 * @param flag - The feature management flag
 * @returns Object containing icon component, icon color, and background color
 */
export function getFeatureManagementIconAndColor(flag: FeatureManagementFlag) {
  const featureConfig = FEATURE_MANAGEMENT_REGISTRY[flag];
  if (!featureConfig) {
    return {
      icon: UserIcon,
      bulkIcon: UserIcon,
      iconColor: GREY[80],
      iconBackground: GREY[20],
    };
  }

  return {
    icon: featureConfig.icon,
    bulkIcon: featureConfig.bulkIcon,
    iconColor: featureConfig.iconColor,
    iconBackground: featureConfig.iconBackground,
  };
}

/**
 * Check if a feature management flag has a configuration component
 * @param flag - The feature management flag
 * @returns True if the feature has a configuration component
 */
export function hasFeatureManagementConfig(
  flag: FeatureManagementFlag
): boolean {
  return !!FEATURE_MANAGEMENT_REGISTRY[flag]?.configComponent;
}

/**
 * Get all feature management flags that have configuration components
 * @returns Array of feature management flags with available configurations
 */
export function getConfigurableFeatureFlags(): FeatureManagementFlag[] {
  return Object.entries(FEATURE_MANAGEMENT_REGISTRY)
    .filter(([, config]) => config.configComponent)
    .map(([flag]) => flag as FeatureManagementFlag);
}

/**
 * Get the turn on modal message for a feature management flag
 * @param flag - The feature management flag
 * @returns The custom turn on message or undefined if not set
 */
export function getFeatureManagementTurnOnMessage(
  flag: FeatureManagementFlag
): string | undefined {
  return FEATURE_MANAGEMENT_REGISTRY[flag]?.turnOnModalMessage;
}

/**
 * Get the turn off modal message for a feature management flag
 * @param flag - The feature management flag
 * @returns The custom turn off message or undefined if not set
 */
export function getFeatureManagementTurnOffMessage(
  flag: FeatureManagementFlag
): string | undefined {
  return FEATURE_MANAGEMENT_REGISTRY[flag]?.turnOffModalMessage;
}

/**
 * Get the drawer details for a feature management flag
 * @param flag - The feature management flag
 * @returns The custom drawer details or undefined if not set
 */
export function getFeatureManagementDrawerDetails(
  flag: FeatureManagementFlag
): string | undefined {
  return FEATURE_MANAGEMENT_REGISTRY[flag]?.drawerDetails;
}

/**
 * Get the required feature flag for a feature management flag
 * @param flag - The feature management flag
 * @returns The required feature flag or undefined if not set
 */
export function getFeatureManagementRequiredFeatureFlag(
  flag: FeatureManagementFlag
): FeatureFlag | undefined {
  return FEATURE_MANAGEMENT_REGISTRY[flag]?.requiredFeatureFlag;
}

/**
 * Get the complete registry entry for a feature management flag
 * @param flag - The feature management flag
 * @returns The complete registry entry or undefined if not found
 */
export function getFeatureManagementRegistryEntry(flag: FeatureManagementFlag) {
  return FEATURE_MANAGEMENT_REGISTRY[flag];
}
